import { trackPromise } from "react-promise-tracker";
import _ from "lodash";
import { Button, IconButton } from "@mui/material";
import moment from "moment/moment";
import apiClient from "../../lib/api";
import urlConstant from "../../constants/urlConstants";
import dataConstant from "../../constants/dataConstants";
import GridComponent from "../../shared-components/gridComponent";
import { useLocation, useNavigate } from "react-router-dom";
import { IconEye } from "@tabler/icons-react";
import SnackState from "../../shared-components/snackbar/snackState";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { selectUserDetail } from "../../redux/slice/userSlice";
import ForecastHoursDetails from "../Submission/forecastHoursDetails";

const PatientList = () => {
  const navigate = useNavigate();
  const [snack, closeSnack, showSnackbar] = SnackState();
  const [filterDropdownValue, setFilterDropdownValue] = useState();
  const fromMarketersLocation = useLocation();
  const userDetails = useSelector(selectUserDetail);
  const { status, marketercode } = fromMarketersLocation.state || {};
  const isAdmin = userDetails?.roles === dataConstant.roles.admin;
  const [selectedRow, setSelectedRow] = useState(null);
  const [openForecastHoursDetailsModal, setForecastHoursDetailsModal] =
    useState(false);

  useEffect(() => {
    trackPromise(
      apiClient
        .get(`${urlConstant.Submission.GetAllFilterDropDowns}`, {
          authRequired: false,
        })
        .then((response) => {
          if (response.status === dataConstant.APIStatus.Success) {
            setFilterDropdownValue(response?.data);
          }
        }),
    );
  }, []);

  const advanceFiltersConfig = [
    {
      type: "date-range",
      label: "Application",
      name: "applicationDate",
    },
    {
      type: "select",
      label: "Community Liaison",
      name: "liaison",
      options: filterDropdownValue?.liasionsData,
    },
    {
      type: "text",
      label: "Referred By",
      name: "refferedBy",
    },
    {
      type: "select",
      label: "Approved By",
      name: "approvedBY",
      options: filterDropdownValue?.usersData,
    },
    {
      type: "date-range",
      label: "Approved",
      name: "approvedDate",
    },
  ];

  const baseColumns = [
    {
      field: "submissionId",
      headerName: "Submission No.",
      flex: 1,
      sortable: true,
    },
    {
      field: "fullName",
      headerName: "Full Name",
      flex: 1,
      sortable: true,
    },
    ...(isAdmin
      ? [
          {
            field: "totalHours",
            headerName: "Forecast Hours",
            flex: 1,
            sortable: false,
            renderCell: (params) => (
              <>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    setSelectedRow(params.row);
                    setForecastHoursDetailsModal(true);
                  }}
                >
                  {params.row.totalHours ?? 0.0}
                </Button>
              </>
            ),
          },
        ]
      : []),
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      sortable: true,
      disableColumnMenu: true,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      sortable: false,
    },
    {
      field: "applicationDate",
      headerName: "Application At",
      flex: 1,
      sortable: true,
    },
    {
      field: "approvedBY",
      headerName: "Approved By",
      flex: 1,
      sortable: true,
    },
    {
      field: "approvedDate",
      headerName: "Approved At",
      flex: 1,
      sortable: true,
    },
  ];

  const actionColumn = {
    field: " ",
    headerName: "",
    sortable: false,
    renderCell: (params) => (
      <>
        <IconButton
          size="small"
          onClick={(e) => {
            navigate(`/clientdetail/${params.row.id}`);
          }}
        >
          <IconEye stroke={1.5} />
        </IconButton>
      </>
    ),
  };

  const columns =
    userDetails?.roles === dataConstant.roles.admin
      ? [actionColumn, ...baseColumns]
      : baseColumns;

  const getGridData = async ({
    page,
    pageSize,
    sortModel,
    searchText,
    filters,
  }) => {
    const sort =
      sortModel.length > 0
        ? {
            sortColumnName: sortModel[0].field,
            sortOrder: sortModel[0].sort === "asc" ? "asc" : "desc",
          }
        : { sortColumnName: "approvedDate", sortOrder: "desc" };
    const requestObject = {
      PagingInfoVM: {
        pageNumber: page,
        rowsPerPage: pageSize,
        searchText: searchText,
        sortColumns: sort,
      },
    };
    if (filters?.applicationDate) {
      requestObject.FromDate = dayjs(filters?.applicationDate?.from).format(
        dataConstant.dateFormat,
      );
      requestObject.ToDate = dayjs(filters?.applicationDate?.to).format(
        dataConstant.dateFormat,
      );
    }
    if (filters?.liaison) {
      requestObject.LiaisonName = filters?.liaison;
    }
    if (filters?.approvedBY) {
      const approvedBy = _.find(
        filterDropdownValue?.usersData,
        (x) => x.name === filters?.approvedBY,
      );
      if (approvedBy !== undefined) {
        requestObject.ApprovedBy = approvedBy.id;
      }
    }
    if (filters?.approvedDate) {
      requestObject.ApprovedFromDate = dayjs(
        filters?.approvedDate?.from,
      ).format(dataConstant.dateFormat);
      requestObject.ApprovedToDate = dayjs(filters?.approvedDate?.to).format(
        dataConstant.dateFormat,
      );
    }
    if (filters?.refferedBy) {
      requestObject.RefferedBy = filters?.refferedBy;
    }
    if (status === "isForApproved" && marketercode) {
      requestObject.MarketerCode = marketercode;
    }
    try {
      const response = await trackPromise(
        apiClient.post(
          urlConstant.WellSky.GetApprovedSubmissionList,
          requestObject,
          { authRequired: true },
        ),
      );

      if (response?.status !== dataConstant.APIStatus.Success) {
        showSnackbar(response?.message, "error");
        return {
          items: [],
          totalCount: 0,
        };
      }
      const decryptedData = response.data?.approvedSubmissionLists?.map(
        (item) => ({
          ...item,
          id: item.unqGuid,
          applicationDate:
            item.applicationDate !== null
              ? moment
                  .utc(item.applicationDate)
                  .tz(dataConstant.centralAmericaTimeZone)
                  .format(dataConstant.dateFormatWithTime)
              : "-",
          approvedDate:
            item.approvedDate !== null
              ? moment
                  .utc(item.approvedDate)
                  .tz(dataConstant.centralAmericaTimeZone)
                  .format(dataConstant.dateFormatWithTime)
              : "-",
        }),
      );
      return {
        items: decryptedData || [],
        totalCount: response.data?.totalCount || 0,
      };
    } catch (err) {
      console.error("API failed", err);
      return {
        items: [],
        totalCount: 0,
      };
    }
  };

  return (
    <>
      <GridComponent
        columns={columns}
        getGridData={getGridData}
        isRequiredAdvanceFilter={true}
        advanceFiltersConfig={advanceFiltersConfig}
      />

      <ForecastHoursDetails
        open={openForecastHoursDetailsModal}
        onClose={() => setForecastHoursDetailsModal(false)}
        scheduleData={selectedRow?.foreCastHours || []}
      />

      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} />
    </>
  );
};

export default PatientList;
